import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import { adminApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import PasswordInput from "../../components/PasswordInput";

export default function AdminSettings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("password");
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Change own password
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwLoading, setPwLoading] = useState(false);

  // Invite admin
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", password: "" });

  // Reset admin password
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  // Cron schedules
  const [cronSchedules, setCronSchedules] = useState([]);
  const [cronLoading, setCronLoading] = useState(false);
  const [cronSaving, setCronSaving] = useState(null);

  // Cloudflare global config
  const [cfConfig, setCfConfig] = useState(null);
  const [cfForm, setCfForm] = useState({ account_id: "", api_token: "" });
  const [cfBusy, setCfBusy] = useState(false);
  const [cfTest, setCfTest] = useState(null);

  useEffect(() => { loadAdmins(); }, []);
  useEffect(() => { if (tab === "schedules") loadCronSchedules(); }, [tab]);
  useEffect(() => { if (tab === "cloudflare") loadCf(); /* eslint-disable-next-line */ }, [tab]);

  async function loadAdmins() {
    try {
      const data = await adminApi.getAdmins();
      setAdmins(data);
    } catch (err) { setError(err.message); }
    setLoading(false);
  }

  async function loadCronSchedules() {
    setCronLoading(true);
    try {
      const data = await adminApi.getCronSchedules();
      setCronSchedules(data);
    } catch (err) { flash(err.message, "error"); }
    setCronLoading(false);
  }

  async function updateSchedule(schedule, updates) {
    setCronSaving(schedule.id);
    try {
      const updated = await adminApi.updateCronSchedule(schedule.id, updates);
      setCronSchedules(prev => prev.map(s => s.id === schedule.id ? updated : s));
      flash("Schedule updated");
    } catch (err) { flash(err.message, "error"); }
    setCronSaving(null);
  }

  async function loadCf() {
    try {
      const c = await adminApi.getCloudflareConfig();
      setCfConfig(c);
      setCfForm({ account_id: c.account_id || "", api_token: "" });
    } catch (err) { flash(err.message, "error"); }
  }
  async function saveCf() {
    setCfBusy(true);
    try { await adminApi.updateCloudflareConfig(cfForm); flash("Cloudflare config saved"); await loadCf(); }
    catch (err) { flash(err.message, "error"); }
    setCfBusy(false);
  }
  async function testCf() {
    setCfBusy(true); setCfTest(null);
    try {
      const r = await adminApi.testCloudflareConfig(cfForm);
      // Test only verifies what's typed — it does NOT persist (so a bad token
      // can't overwrite a good stored one). Keep the form intact so the admin
      // can click Save; don't reload (that would clear the typed token).
      setCfTest({ ok: true, msg: `Connection OK — token ${r.status || "active"}. Click Save to store it.` });
    } catch (err) { setCfTest({ ok: false, msg: err.message }); }
    setCfBusy(false);
  }

  function flash(msg, type = "success") {
    if (type === "success") { setSuccess(msg); setError(""); }
    else { setError(msg); setSuccess(""); }
    setTimeout(() => { setSuccess(""); setError(""); }, 4000);
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) { flash("Passwords do not match", "error"); return; }
    if (pwForm.newPassword.length < 6) { flash("Password must be at least 6 characters", "error"); return; }
    setPwLoading(true);
    try {
      await adminApi.changeSelfPassword(pwForm.currentPassword, pwForm.newPassword);
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      flash("Password changed successfully");
    } catch (err) { flash(err.message, "error"); }
    setPwLoading(false);
  }

  async function handleInviteAdmin(e) {
    e.preventDefault();
    if (inviteForm.password.length < 6) { flash("Password must be at least 6 characters", "error"); return; }
    try {
      await adminApi.createAdmin(inviteForm);
      setInviteForm({ name: "", email: "", password: "" });
      setShowInvite(false);
      flash("Admin user created successfully");
      loadAdmins();
    } catch (err) { flash(err.message, "error"); }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (resetPassword.length < 6) { flash("Password must be at least 6 characters", "error"); return; }
    try {
      await adminApi.changeAdminPassword(resetTarget.id, resetPassword);
      setResetTarget(null);
      setResetPassword("");
      flash("Password reset successfully");
    } catch (err) { flash(err.message, "error"); }
  }

  const tabs = [
    { id: "password", label: "My Password", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg> },
    { id: "admins", label: "Admin Users", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
    { id: "schedules", label: "Sync Schedules", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> },
    { id: "cloudflare", label: "Cloudflare", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><path d="M17.5 19a4.5 4.5 0 1 0 0-9h-1.26A7 7 0 1 0 4 15.25"/></svg> },
  ];

  return (
    <AdminLayout>
      <div className="page-header">
        <h1>Settings</h1>
        <p className="page-subtitle">Manage your account and admin users</p>
      </div>

      {error && <div className="toast toast-error">{error}</div>}
      {success && <div className="toast toast-success">{success}</div>}

      <div className="settings-tabs">
        {tabs.map((t) => (
          <button key={t.id} className={`settings-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Change Own Password */}
      {tab === "password" && (
        <div className="settings-section">
          <div className="settings-card">
            <div className="settings-card-header">
              <h3>Change Your Password</h3>
              <p>Update the password for <strong>{user?.email}</strong></p>
            </div>
            <form onSubmit={handleChangePassword} className="settings-form">
              <div className="form-field">
                <label>Current Password</label>
                <PasswordInput value={pwForm.currentPassword} onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })} placeholder="Enter current password" required />
              </div>
              <div className="form-field">
                <label>New Password</label>
                <PasswordInput value={pwForm.newPassword} onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })} placeholder="Minimum 6 characters" required />
              </div>
              <div className="form-field">
                <label>Confirm New Password</label>
                <PasswordInput value={pwForm.confirmPassword} onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })} placeholder="Re-enter new password" required />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={pwLoading}>{pwLoading ? "Saving..." : "Update Password"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Admin Users Management */}
      {tab === "admins" && (
        <div className="settings-section">
          <div className="toolbar" style={{ marginBottom: "1.5rem" }}>
            <div className="toolbar-left">
              <h3 style={{ margin: 0 }}>{admins.length} admin user{admins.length !== 1 ? "s" : ""}</h3>
            </div>
            <div className="toolbar-right">
              <button className="btn btn-primary" onClick={() => setShowInvite(true)}>+ Invite Admin</button>
            </div>
          </div>

          {loading ? (
            <div className="loading-state"><div className="spinner" /></div>
          ) : (
            <div className="admin-list">
              {admins.map((a) => (
                <div key={a.id} className="admin-row">
                  <div className="admin-row-avatar">
                    {(a.name || a.email).charAt(0).toUpperCase()}
                  </div>
                  <div className="admin-row-info">
                    <span className="admin-row-name">{a.name || "Unnamed"}</span>
                    <span className="admin-row-email">{a.email}</span>
                  </div>
                  <div className="admin-row-meta">
                    {a.id === user?.id && <span className="badge active">You</span>}
                    <span className="badge" style={{ opacity: 0.7 }}>Admin</span>
                  </div>
                  <div className="admin-row-actions">
                    {a.id !== user?.id && (
                      <button className="btn btn-sm btn-warning" onClick={() => { setResetTarget(a); setResetPassword(""); }}>
                        Reset Password
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sync Schedules */}
      {tab === "schedules" && (
        <div className="settings-section">
          <div className="settings-card">
            <div className="settings-card-header">
              <h3>Background Sync Schedules</h3>
              <p>Configure how often reference data is synced from gaming providers. Uses standard <a href="https://crontab.guru/" target="_blank" rel="noopener noreferrer" style={{ color: "var(--accent)" }}>cron expressions</a>.</p>
            </div>

            {cronLoading ? (
              <div className="loading-state"><div className="spinner" /></div>
            ) : (
              <div className="cron-list">
                {cronSchedules.map((s) => (
                  <div key={s.id} className="cron-card">
                    <div className="cron-card-header">
                      <div>
                        <div className="cron-card-title">{s.job_name.replace(/_/g, " ")}</div>
                        {s.description && <div className="cron-card-desc">{s.description}</div>}
                      </div>
                      <span className={`cron-badge-enabled ${s.enabled ? "on" : "off"}`}>
                        {s.enabled ? "Active" : "Paused"}
                      </span>
                    </div>
                    <div className="cron-card-fields">
                      <input
                        defaultValue={s.cron_expr}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val && val !== s.cron_expr) updateSchedule(s, { cron_expr: val });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.target.blur();
                        }}
                        placeholder="*/15 * * * *"
                      />
                      <button
                        className={`btn btn-sm ${s.enabled ? "btn-warning" : "btn-success"}`}
                        onClick={() => updateSchedule(s, { enabled: !s.enabled })}
                        disabled={cronSaving === s.id}
                      >
                        {cronSaving === s.id ? "..." : s.enabled ? "Pause" : "Enable"}
                      </button>
                    </div>
                  </div>
                ))}
                {cronSchedules.length === 0 && (
                  <p style={{ color: "var(--text-muted)" }}>No sync schedules configured.</p>
                )}
              </div>
            )}
          </div>
          <div className="info-box" style={{ marginTop: "1rem", maxWidth: "800px" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>Changes take effect immediately — no restart required. Common patterns: <code>*/15 * * * *</code> (every 15 min), <code>0 * * * *</code> (hourly), <code>0 */6 * * *</code> (every 6 hours).</span>
          </div>
        </div>
      )}

      {/* Cloudflare global config */}
      {tab === "cloudflare" && (
        <div className="settings-section">
          <div className="settings-card">
            <div className="settings-card-header">
              <h3>Cloudflare API</h3>
              <p>One account-level token used to provision every venue's IGT tunnel. Create a <strong>scoped</strong> API token (dash.cloudflare.com → My Profile → API Tokens → Create Custom Token) with <strong>Account → Cloudflare Tunnel: Edit</strong> and <strong>Zone → DNS: Edit</strong>.</p>
            </div>
            <div className="settings-form">
              <div className="form-field">
                <label>Account ID</label>
                <input value={cfForm.account_id} onChange={(e) => setCfForm({ ...cfForm, account_id: e.target.value })} placeholder="e.g. f18341897a423186da4f45a2c6b548eb" />
              </div>
              <div className="form-field">
                <label>API Token</label>
                <PasswordInput value={cfForm.api_token} onChange={(e) => setCfForm({ ...cfForm, api_token: e.target.value })}
                  placeholder={cfConfig?.token_set ? `${cfConfig.token_hint} — leave blank to keep` : "Paste the scoped API token"} />
              </div>
              <div className="form-actions" style={{ gap: "0.5rem" }}>
                <button type="button" className="btn btn-primary" onClick={saveCf} disabled={cfBusy}>{cfBusy ? "…" : "Save"}</button>
                <button type="button" className="btn btn-outline" onClick={testCf} disabled={cfBusy}>Test connection</button>
              </div>
              {cfTest && (
                <div className={`toast ${cfTest.ok ? "toast-success" : "toast-error"}`} style={{ marginTop: "0.25rem" }}>{cfTest.msg}</div>
              )}
            </div>
          </div>
          <div className="info-box" style={{ marginTop: "1rem", maxWidth: 800 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>Used by the per-venue <strong>Cloudflare Tunnel</strong> panel (IGT venue → Settings → IGT Tunnel). Admin-only; the token is never shown again after saving.</span>
          </div>
        </div>
      )}

      {/* Invite Admin Modal */}
      {showInvite && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowInvite(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div className="modal-header-text">
                <h2>Invite Admin User</h2>
                <p>Create a new administrator account</p>
              </div>
              <button type="button" className="modal-close-btn" onClick={() => setShowInvite(false)} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <form onSubmit={handleInviteAdmin}>
              <div className="modal-body">
              <div className="form-field">
                <label>Full Name</label>
                <input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="John Smith" />
              </div>
              <div className="form-field">
                <label>Email <span className="required">*</span></label>
                <input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="admin@example.com" required />
              </div>
              <div className="form-field">
                <label>Temporary Password <span className="required">*</span></label>
                <PasswordInput value={inviteForm.password} onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })} placeholder="Minimum 6 characters" required />
              </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create Admin</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setResetTarget(null)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-header-icon" style={{ background: "rgba(245,158,11,0.12)", color: "var(--warning)" }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              </div>
              <div className="modal-header-text">
                <h2>Reset Password</h2>
                <p>Set a new password for <strong>{resetTarget.name || resetTarget.email}</strong></p>
              </div>
              <button type="button" className="modal-close-btn" onClick={() => setResetTarget(null)} aria-label="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="modal-body">
                <div className="form-field">
                  <label>New Password <span className="required">*</span></label>
                  <PasswordInput value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="Minimum 6 characters" required />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setResetTarget(null)}>Cancel</button>
                <button type="submit" className="btn btn-warning">Reset Password</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .settings-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 2rem;
          border-bottom: 2px solid var(--border);
          padding-bottom: 0;
        }
        .settings-tab {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          color: var(--text-muted);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s;
        }
        .settings-tab:hover { color: var(--text); }
        .settings-tab.active {
          color: var(--accent);
          border-bottom-color: var(--accent);
        }
        .settings-section { max-width: 800px; }
        .settings-form { display: flex; flex-direction: column; gap: 1rem; }

        .admin-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .admin-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.25rem;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: 10px;
          transition: border-color 0.2s;
        }
        .admin-row:hover { border-color: var(--accent); }
        .admin-row-avatar {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: linear-gradient(135deg, var(--accent), #6366f1);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1rem;
          flex-shrink: 0;
        }
        .admin-row-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .admin-row-name {
          font-weight: 600;
          color: var(--text);
          font-size: 0.95rem;
        }
        .admin-row-email {
          color: var(--text-muted);
          font-size: 0.85rem;
        }
        .admin-row-meta {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }
        .admin-row-actions {
          flex-shrink: 0;
        }

        .cron-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .cron-card {
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          padding: 1.25rem;
          transition: border-color 0.2s;
        }
        .cron-card:hover { border-color: var(--accent); }
        .cron-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.75rem;
        }
        .cron-card-title {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--text);
        }
        .cron-card-desc {
          font-size: 0.82rem;
          color: var(--text-muted);
          margin-top: 0.15rem;
        }
        .cron-card-fields {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .cron-card-fields input {
          font-family: "SF Mono", "Fira Code", monospace;
          font-size: 0.9rem;
          width: 180px;
        }
        .cron-badge-enabled {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.72rem;
          font-weight: 600;
          padding: 0.2rem 0.6rem;
          border-radius: 10px;
        }
        .cron-badge-enabled.on {
          background: var(--success-soft);
          color: var(--success);
        }
        .cron-badge-enabled.off {
          background: var(--bg-elevated);
          color: var(--text-muted);
        }
      `}</style>
    </AdminLayout>
  );
}
